/**
 * @fileoverview Hook for creating palette mode context value. Used by
 * AppThemeProvider to construct the context value.
 */

import { useCallback, useMemo } from 'react';
import type { PaletteMode } from '@mui/material';
import type { PaletteModeContextValue } from '../types';

export type { PaletteModeContextValue };

/**
 * Creates a memoised palette mode context value with symmetric operations.
 */
export function usePaletteModeContext(
  paletteMode: PaletteMode,
  setPaletteMode: (mode: PaletteMode | 'system') => void,
  isDualTheme: boolean,
): PaletteModeContextValue {
  const togglePaletteMode = useCallback(() => {
    setPaletteMode(paletteMode === 'dark' ? 'light' : 'dark');
  }, [paletteMode, setPaletteMode]);

  return useMemo(
    () => ({
      paletteMode,
      setPaletteMode,
      togglePaletteMode,
      isDualTheme,
    }),
    [isDualTheme, paletteMode, setPaletteMode, togglePaletteMode],
  );
}
